import { useEffect, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import useHomeContext from '@/hooks/useHomeContext';
import {
  SortDirection,
  TGetBusinessOrAccountTransactionsRequestParams,
  TransactionHistorySortByFieldEnum,
  TTransactionListPaginatedResponse
} from '@/services/types/TransactionData';
import { SortDirectionEnum, TTransactionHistory } from '../types/TransactionHistory';
import { TDate } from '@/context/HomeProvider/HomeContextProvider';
import { AccountTypeEnum } from '@/context/types/Account';
import { getAccountTransactions, getBusinessTransactions } from '@/services/transactionHistory';
import { transformTransactionHistoryData } from '../utils/transformTransactionHistoryData';
import {
  extractBankTransactionFilter,
  extractTransactionFilter
} from '@/utils/transactionHistoryFilter';

const DEFAULT_ITEMS_PER_PAGE = 20;

export const useTransactionHistoryV2 = () => {
  const { business, accountId, accountType } = useAuth()!;
  const {
    dateFilter,
    transactionFilter,
    teamMemberFilter,
    accountsFilter,
    wallets,
    cards,
    setIsLoading,
    setCurrentPageTransactions
  } = useHomeContext()!;
  const maxItemsPerPage = DEFAULT_ITEMS_PER_PAGE;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(0);
  const [transactionsTotalCount, setTransactionsTotalCount] = useState(0);

  const [previousFilters, setPreviousFilters] = useState<(string | number)[]>([]);
  const [dateSortingDirection, setDateSortingDirection] = useState<SortDirection>(
    SortDirectionEnum.DESC
  );

  const [currDateFilter, setCurrDateFilter] = useState<{
    startDate: TDate;
    endDate: TDate;
  }>();

  useEffect(() => {
    if (dateFilter?.startDate && dateFilter?.endDate) {
      const currentFilters = [...accountsFilter, ...teamMemberFilter, ...transactionFilter];

      if (
        hasFiltersChanged(previousFilters, currentFilters) ||
        hasDateFiltersChanged({
          currentStartDate: dateFilter.startDate,
          currentEndDate: dateFilter.endDate,
          previousStartDate: currDateFilter?.startDate,
          previousEndDate: currDateFilter?.endDate
        })
      ) {
        setCurrDateFilter({ startDate: dateFilter.startDate, endDate: dateFilter.endDate });
        setPreviousFilters(currentFilters);
        setCurrentPage(1);
        setIsLoading(true);
        return;
      }

      const getTransactionHistory = async (
        id: string,
        options: TGetBusinessOrAccountTransactionsRequestParams
      ) => {
        let response: TTransactionListPaginatedResponse = {
          totalElements: 0,
          transactions: [],
          pageNumber: 1,
          pageSize: DEFAULT_ITEMS_PER_PAGE
        };
        switch (accountType) {
          case AccountTypeEnum.PERSONAL: {
            response = (await getAccountTransactions(id, options)).data;
            break;
          }

          case AccountTypeEnum.BUSINESS: {
            response = (await getBusinessTransactions(id, options)).data;
          }
        }

        setPageSize(response.pageSize);
        setTransactionsTotalCount(response.totalElements);

        setCurrentPageTransactions(
          response.transactions.map((transaction): TTransactionHistory => {
            const { generalLedgerCode, customer, ...transactionData } = transaction.data;
            const transformedTransaction: TTransactionHistory = {
              ...transaction,
              details: {
                transactionId: transactionData.id,
                walletId: transactionData.walletId,
                customer: customer,
                attachments: [],
                readOnly: false,
                generalLedgerCode
              },
              wallet: undefined,
              card: undefined,
              toWallet: undefined
            };

            return transformTransactionHistoryData({
              transformedTransaction,
              cards,
              wallets
            });
          })
        );
        setIsLoading(false);
      };

      const filters: TGetBusinessOrAccountTransactionsRequestParams = {
        startDate: dateFilter?.startDate
          ? new Date(dateFilter?.startDate).toISOString()
          : undefined,
        endDate: dateFilter?.endDate ? new Date(dateFilter?.endDate).toISOString() : undefined,
        walletIds: accountsFilter.length ? accountsFilter : undefined,
        customerIds: teamMemberFilter.length ? teamMemberFilter : undefined,
        transactionTypes: transactionFilter.length
          ? extractTransactionFilter(transactionFilter)
          : undefined,
        bankTransactionTypes: transactionFilter.length
          ? extractBankTransactionFilter(transactionFilter)
          : undefined,
        sortByField: TransactionHistorySortByFieldEnum.Date,
        sortByDirection: dateSortingDirection,
        page: currentPage - 1,
        size: pageSize
      };

      setIsLoading(true);

      switch (accountType) {
        case AccountTypeEnum.PERSONAL: {
          if (accountId) {
            getTransactionHistory(accountId!, filters);
          }

          break;
        }

        case AccountTypeEnum.BUSINESS: {
          if (business?.id) {
            getTransactionHistory(business!.id, filters);
          }
        }
      }
    }
  }, [
    business?.id,
    accountId,
    accountType,
    dateFilter,
    currDateFilter,
    transactionFilter,
    teamMemberFilter,
    accountsFilter,
    dateSortingDirection,
    currentPage,
    previousFilters,
    wallets,
    cards
  ]);

  return {
    transactionsTotalCount,
    dateSortingDirection,
    setDateSortingDirection,
    currentPage,
    setCurrentPage,
    maxItemsPerPage
  };
};

const hasDateFiltersChanged = (data: {
  currentStartDate?: TDate;
  currentEndDate?: TDate;
  previousStartDate?: TDate;
  previousEndDate?: TDate;
}) =>
  data.currentStartDate &&
  data.currentEndDate &&
  data.previousStartDate !== data.currentStartDate &&
  data.previousEndDate !== data.currentEndDate;

const hasFiltersChanged = (
  previousFilters: (string | number)[],
  currentFilters: (string | number)[]
) =>
  (previousFilters.length === 1 &&
    currentFilters.length === 1 &&
    previousFilters[0] !== currentFilters[0]) ||
  previousFilters.length !== currentFilters.length;
